// Document preview: PDF via pdf.js, PNG/JPEG natively, TIFF decoded to a canvas (design 3.1).
import { useEffect, useRef, useState, type RefObject } from 'react';
import { Document, Page } from 'react-pdf';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import * as UTIF from 'utif';

import { pdfOptions } from '../lib/pdfjsWorker';

function TiffView({ url, name }: { url: string; name: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => r.arrayBuffer())
      .then((buf) => {
        if (cancelled || !ref.current) return;
        const ifds = UTIF.decode(buf);
        const first = ifds[0];
        if (!first) throw new Error('empty');
        UTIF.decodeImage(buf, first);
        const rgba = UTIF.toRGBA8(first);
        const canvas = ref.current;
        canvas.width = first.width;
        canvas.height = first.height;
        const ctx = canvas.getContext('2d')!;
        ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), first.width, first.height), 0, 0);
      })
      .catch(() => setError(true));
    return () => {
      cancelled = true;
    };
  }, [url]);
  if (error) return <Box>The TIFF could not be displayed.</Box>;
  return <canvas ref={ref} role="img" aria-label={name} style={{ maxWidth: '100%' }} />;
}

const MAX_PDF_WIDTH = 560;

/**
 * Width available to the PDF page, capped at MAX_PDF_WIDTH. A fixed 560 px page overflowed the column at a 768 px
 * viewport and made the whole page scroll sideways (requirement 9.1, task 12.2).
 */
function useAvailableWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(MAX_PDF_WIDTH);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.floor(entry?.contentRect.width ?? MAX_PDF_WIDTH);
      if (w > 0) setWidth(Math.min(MAX_PDF_WIDTH, w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** `goTo` moves the PDF preview to a page (a new `seq` repeats the jump), for example a segment's first page. */
export function DocumentViewer({
  url,
  contentType,
  name,
  goTo,
}: {
  url: string;
  contentType: string;
  name: string;
  goTo?: { page: number; seq: number };
}) {
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [box, width] = useAvailableWidth();
  // A new jump request moves the page (state adjusted during render, not in an effect).
  const [jumpSeq, setJumpSeq] = useState(goTo?.seq);
  if (goTo && goTo.seq !== jumpSeq) {
    setJumpSeq(goTo.seq);
    setPage(Math.max(1, pages ? Math.min(goTo.page, pages) : goTo.page));
  }
  if (contentType === 'application/pdf') {
    return (
      <SpaceBetween size="s">
        <div ref={box} style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Document
            file={url}
            options={pdfOptions}
            onLoadSuccess={(d) => setPages(d.numPages)}
            loading={<Spinner />}
            error={<Box>The PDF could not be displayed.</Box>}
          >
            <Page pageNumber={page} width={width} renderAnnotationLayer={false} renderTextLayer={false} />
          </Document>
        </div>
        {pages > 1 && (
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Button
              iconName="angle-left"
              variant="icon"
              ariaLabel="Previous page"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            />
            <Box>
              <span aria-live="polite">
                Page {page} of {pages}
              </span>
            </Box>
            <Button
              iconName="angle-right"
              variant="icon"
              ariaLabel="Next page"
              disabled={page >= pages}
              onClick={() => setPage(page + 1)}
            />
          </SpaceBetween>
        )}
      </SpaceBetween>
    );
  }
  if (contentType === 'image/tiff') return <TiffView url={url} name={name} />;
  return <img src={url} alt={name} style={{ maxWidth: '100%' }} />;
}
